import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import type { AttributeField } from "@/features/properties/types/propertyAttributes";
import { fetchResolvedSurveyAttributes } from "@/features/properties/hooks/usePropertyAttributes";

import {
  findExactField,
  findFieldValue,
  getNodeFieldValues,
  getSfPerTonFromClimateZone,
  summariseNodeValues,
} from "./usePropertyOverviewData/attributeSearch";
import { computeCompletionDetails } from "./usePropertyOverviewData/computeCompletion";
import { computeCostSections } from "./usePropertyOverviewData/computeCostSections";
import { computeHvacTonnage } from "./usePropertyOverviewData/computeHvacTonnage";
import type { OverviewKpiData } from "./usePropertyOverviewData/types";

export type { CostLineItem, CostSection, KpiStatus, OverviewKpiData } from "./usePropertyOverviewData/types";
export {
  getElectricalStatus,
  getHvacStatus,
  getParkingStatus,
  getRenewalScoreTier,
  getRoofStatus,
} from "./usePropertyOverviewData/kpiStatus";

const FOOT_LOCKER_CLIENT_ID = "60642cb5-6653-47f7-921f-858c401290ac";

export function usePropertyOverviewData(propertyId: string | undefined, clientId?: string) {
  return useQuery({
    queryKey: ["property-overview-kpi", propertyId, clientId],
    queryFn: async (): Promise<OverviewKpiData> => {
      if (!propertyId) throw new Error("No propertyId");

      // ── 1. Fetch property basics ───────────────────────────────────
      const { data: propData, error: propError } = await supabase
        .from("properties")
        .select("square_footage, project_id, climate_zone, hvac_tonnage, electrical_panel")
        .eq("id", propertyId)
        .maybeSingle();

      if (propError) throw propError;

      const resolvedClientId = clientId ?? propData?.project_id ?? null;

      // ── 1b. Fetch completion score via RPC (non-fatal) ─────────────
      let completionScore: number | null = null;
      let completionDetails: Record<string, number> | null = null;
      try {
        const { data: rpcScore } = await supabase.rpc(
          "calculate_property_completion_score",
          { prop_id: propertyId }
        );
        if (typeof rpcScore === "number") {
          completionScore = rpcScore;
        }
      } catch {
        // RPC may not exist — silently fail
      }

      // ── 2. Fetch HVAC tonnage (sum of tonnage for HVAC assets) ──
      const { data: hvacAssets, error: hvacError } = await supabase
        .from("property_assets")
        .select(`
          name,
          estimated_age_years,
          category_attributes,
          category_id,
          asset_categories (
            name
          )
        `)
        .eq("property_id", propertyId);

      if (hvacError) throw hvacError;

      let { hvacTonnageInstalled, oldestHvacUnitAgeYears } = computeHvacTonnage(hvacAssets as any);

      // ── 3. Fetch survey attributes (prod_data when complete, else data) ──
      const attributes: AttributeField[] = await fetchResolvedSurveyAttributes(propertyId);

      // ── 3b. Compute data quality metrics for completion card ───────
      try {
        const { data: allAssets } = await supabase
          .from("property_assets")
          .select("id, manufacturer, model_number, model_name, serial_number")
          .eq("property_id", propertyId);

        const result = computeCompletionDetails(
          (allAssets ?? []) as any,
          attributes,
          resolvedClientId
        );
        if (result.completionDetails) {
          completionDetails = result.completionDetails;
          completionScore = result.completionScore;
        }
      } catch {
        // Non-fatal — leave completion as null
      }

      // ── 4. Search attributes for KPI fields ───────────────────────

      // Square Footage — fall back to attribute search if DB column is null
      let squareFootage: number | null = (() => {
        const raw = propData?.square_footage;
        if (raw == null) return null;
        const parsed = Number(String(raw).replace(/[^0-9.]/g, ""));
        return !isNaN(parsed) && parsed > 0 ? parsed : null;
      })();
      if (squareFootage === null && attributes.length > 0) {
        const sfRaw = findFieldValue(attributes, [
          "total building sf",
          "building sf",
          "total building",
          "square footage",
          "square feet",
          "building size",
          "sqft",
          "sq ft",
          "gross area",
          "building area",
          "total area",
          "total sf",
        ]);
        if (sfRaw !== null) {
          const parsed = Number(String(sfRaw).replace(/[^0-9.]/g, ""));
          if (!isNaN(parsed) && parsed > 0) {
            squareFootage = parsed;
          }
        }
      }

      // HVAC tonnage — prefer property column, then assets, then attributes
      if ((propData as any)?.hvac_tonnage) {
        const parsed = Number(String((propData as any).hvac_tonnage).replace(/[^0-9.]/g, ""));
        if (!isNaN(parsed) && parsed > 0) {
          hvacTonnageInstalled = parsed;
        }
      }
      if (hvacTonnageInstalled === null && resolvedClientId === FOOT_LOCKER_CLIENT_ID && attributes.length > 0) {
        // For this client, use the survey "Tonnage" field directly (exact match to avoid "Estimated tonnage")
        const tonnageRaw = findExactField(attributes, "tonnage");
        if (tonnageRaw !== null) {
          const parsed = Number(String(tonnageRaw).replace(/[^0-9.]/g, ""));
          if (!isNaN(parsed) && parsed > 0) {
            hvacTonnageInstalled = parsed;
          }
        }
      } else if (hvacTonnageInstalled === null) {
        const tonnageRaw = findFieldValue(attributes, [
          "installed tonnage",
          "total tonnage",
          "tonnage",
          "hvac tonnage",
          "total number of installed tonnage",
        ]);
        if (tonnageRaw !== null) {
          const parsed = Number(String(tonnageRaw).replace(/[^0-9.]/g, ""));
          if (!isNaN(parsed) && parsed > 0) {
            hvacTonnageInstalled = parsed;
          }
        }
      }

      // Climate Zone — prefer property column, then attribute search
      let climateZone = (propData as any)?.climate_zone || null;
      if (!climateZone) {
        const climateZoneRaw = findFieldValue(attributes, [
          "climate zone",
          "climate",
          "ashrae",
        ]);
        climateZone = climateZoneRaw ? String(climateZoneRaw) : null;
      }

      // SF per ton — search attributes first, fall back to climate zone lookup
      const sfPerTonRaw = findFieldValue(attributes, [
        "sf per ton",
        "sf/ton",
        "square feet per ton",
        "sqft per ton",
      ]);
      const sfPerTon = sfPerTonRaw
        ? Number(sfPerTonRaw)
        : getSfPerTonFromClimateZone(climateZone);

      // For this client, HVAC adequacy is based on sales room SF, not total building SF
      let hvacSquareFootage = squareFootage;

      if (resolvedClientId === FOOT_LOCKER_CLIENT_ID && attributes.length > 0) {
        const salesSfRaw = findFieldValue(attributes, [
          "sales floor square footage",
          "sales room square footage",
          "sales floor sf",
          "sales room sf",
          "showroom square footage",
          "showroom sf",
          "retail square footage",
          "retail sf",
        ]);

        if (salesSfRaw !== null) {
          const parsed = Number(String(salesSfRaw).replace(/[^0-9.]/g, ""));
          if (!isNaN(parsed) && parsed > 0) {
            hvacSquareFootage = parsed;
          }
        }
      }

      const minTonnageRequired =
        hvacSquareFootage && sfPerTon ? hvacSquareFootage / sfPerTon : null;

      const hvacDeficit =
        hvacTonnageInstalled !== null && minTonnageRequired !== null
          ? hvacTonnageInstalled - minTonnageRequired
          : null;

      // Rooftop unit age — also check attribute tree for text like "20+ Years", "15 years", etc.
      const rtuAgeRaw = findFieldValue(attributes, [
        "age of oldest rooftop",
        "rooftop unit age",
        "rtu age",
        "age of rtu",
        "hvac age",
        "age of hvac",
        "oldest rooftop",
        "oldest rtu",
      ]);
      if (rtuAgeRaw !== null) {
        const ageStr = String(rtuAgeRaw);
        // Parse patterns: "20+ Years", "15 years", "10+", "~25 yrs", "over 20 years"
        const ageMatch = ageStr.match(/(\d+)/);
        if (ageMatch) {
          const parsedAge = parseInt(ageMatch[1], 10);
          if (!isNaN(parsedAge) && (oldestHvacUnitAgeYears === null || parsedAge > oldestHvacUnitAgeYears)) {
            oldestHvacUnitAgeYears = parsedAge;
          }
        }
      }

      // Building dimensions
      const dimensionsRaw = findFieldValue(attributes, [
        "dimension",
        "building dimension",
        "length",
        "width",
      ]);
      const buildingDimensions = dimensionsRaw ? String(dimensionsRaw) : null;

      // Electrical Panel — prefer property column, then attribute search
      let electricalPanel = (propData as any)?.electrical_panel || null;
      if (!electricalPanel) {
        const ampherageRaw = findFieldValue(attributes, [
          "ampherage",
          "amperage",
          "total ampherage",
          "total amperage",
          "amps",
          "panel amps",
        ]);
        electricalPanel = ampherageRaw ? String(ampherageRaw) : null;
      }

      const panelAgeRaw = findFieldValue(attributes, [
        "panel age",
        "estimated panel age",
        "electrical panel age",
        "electrical age",
      ]);
      const electricalStatus = panelAgeRaw ? String(panelAgeRaw) : null;
      const electricalNotes: string | null = null;

      // Roof — use direct field search for "estimated roof age"
      const roofAgeRaw = findFieldValue(attributes, [
        "roof age",
        "estimated roof age",
        "roof condition",
      ]);
      let roofCondition: string | null = null;
      let roofMaterial: string | null = null; // repurposed as roof age
      const roofNotes: string | null = null;

      if (roofAgeRaw) {
        const roofStr = String(roofAgeRaw);
        // Value may be "0–5 years — like new condition"
        const dashIdx = roofStr.indexOf("—");
        if (dashIdx > -1) {
          roofMaterial = roofStr.substring(0, dashIdx).trim();   // age part
          roofCondition = roofStr.substring(dashIdx + 1).trim(); // condition part
        } else {
          roofMaterial = roofStr;
        }
      }

      // Parking Lot
      const parkingValues = getNodeFieldValues(attributes, [
        "parking lot",
        "parking",
        "asphalt",
      ]);
      let parkingLotCondition: string | null = null;
      let parkingLotNotes: string | null = null;

      if (Object.keys(parkingValues).length > 0) {
        // Prioritise overall pavement score (PASER) as the condition indicator
        const summary = summariseNodeValues(
          parkingValues,
          ["paser", "overall", "pavement", "score", "surface"],
          [], // no material for parking
          ["condition", "crack", "seal", "ada", "paint", "cart", "stripe", "bollard", "sidewalk", "curbing", "striping", "drainage", "notes", "scope"]
        );
        parkingLotCondition = summary.condition || summary.material;
        parkingLotNotes = summary.notes;
      }

      // ── 5. Camera Coverage ──────────────────────────────────────────
      let cameraCoverageCondition: string | null = null;
      let cameraCoverageNotes: string | null = null;

      // For this client, use the detailed "Camera Coverage Assessment" field
      if (resolvedClientId === FOOT_LOCKER_CLIENT_ID && attributes.length > 0) {
        const assessmentRaw = findFieldValue(attributes, [
          "camera coverage assessment",
        ]);
        if (assessmentRaw !== null && String(assessmentRaw).trim() !== "") {
          cameraCoverageCondition = String(assessmentRaw);
        }
      }

      // Fallback: generic camera lookup for other clients or if assessment not found
      if (cameraCoverageCondition === null) {
        const cameraRaw = findFieldValue(attributes, [
          "camera coverage",
          "security camera coverage",
          "security camera",
        ]);

        if (cameraRaw !== null) {
          // Handle boolean values (e.g. true → "Yes", false → "No")
          if (typeof cameraRaw === "boolean") {
            cameraCoverageCondition = cameraRaw ? "Yes" : "No";
          } else {
            const camStr = String(cameraRaw);
            // e.g. "2 – Good – Coverage present, 1–2 cameras appear offline"
            const parts = camStr.split(/\s*[–-]\s*/);
            if (parts.length >= 2) {
              cameraCoverageCondition = parts[1]; // "Good"
              cameraCoverageNotes = parts.length >= 3 ? parts.slice(2).join(" – ") : null;
            } else {
              cameraCoverageCondition = camStr;
            }
          }
        }
      }

      // ── 6. Cost estimate items (from property_cost_estimate_items) ──
      let costSections: OverviewKpiData["costSections"] = [];
      let totalReplacementCost = 0;
      const noActionCount = 0;

      try {
        const { data: costItems } = await supabase
          .from("property_cost_estimate_items")
          .select("category, priority, description, cost, sort_order, run_id")
          .eq("property_id", propertyId)
          .order("computed_at", { ascending: false });

        const result = computeCostSections(
          (costItems ?? []) as any,
          hvacDeficit,
          oldestHvacUnitAgeYears
        );
        costSections = result.costSections;
        totalReplacementCost = result.totalReplacementCost;
      } catch (e) {
        console.warn("Cost estimate items query failed:", e);
      }

      // ── Adjust HVAC completion if tonnage is missing ──────────────
      // Tonnage isn't part of the survey form fields, so the attribute-based
      // completion can reach 100% even when we have no tonnage data.
      if (hvacTonnageInstalled === null && completionDetails?.["HVAC"] != null) {
        completionDetails["HVAC"] = Math.min(completionDetails["HVAC"], 90);
        const values = Object.values(completionDetails);
        completionScore = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
      }

      return {
        squareFootage,
        buildingDimensions,
        climateZone,
        sfPerTon,
        hvacTonnageInstalled,
        hvacDeficit,
        oldestHvacUnitAgeYears,
        minTonnageRequired,
        hvacSquareFootage,
        electricalPanel,
        electricalStatus,
        electricalNotes,
        roofMaterial,
        roofCondition,
        roofNotes,
        parkingLotCondition,
        parkingLotNotes,
        cameraCoverageCondition,
        cameraCoverageNotes,
        costSections,
        totalReplacementCost,
        noActionCount,
        completionScore,
        completionDetails,
      };
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}
